/*
 * The typed API client.
 *
 * Thin on purpose. It resolves an operation from the generated manifest, attaches a bearer token,
 * and turns a non-2xx into a typed error. It does not cache, retry, or hold state — TanStack
 * Query does all three in the app (technology-selection §9: "TanStack Query as the single
 * data-fetching idiom in both apps"), and a client that also cached would give the app two
 * caches that disagree.
 *
 * The token arrives as an async getter rather than a value, because access tokens expire and the
 * caller is the only thing that knows how to get a fresh one. A client holding a token would hold
 * a stale one.
 */

import { API_OPERATIONS } from './generated/api.js';
import type { OperationId, OperationTypes } from './generated/api.js';

export interface ApiClientOptions {
  /** Where the API lives. Comes from env in the app; CLAUDE.md forbids compiling it in. */
  readonly baseUrl: string;
  /** Returns a current access token, or null when nobody is signed in. */
  readonly getToken: () => Promise<string | null> | string | null;
  /** Injected in tests. Defaults to global fetch. */
  readonly fetch?: typeof globalThis.fetch;
}

/**
 * A failed call, carrying the status so callers can branch on *why*.
 *
 * 401 and 403 mean different things here and the difference matters to the UI: 401 is "the
 * credential is not valid for this route" and should send the user back through sign-in, while
 * 403 is "this principal has no membership or no entitlement" and must not, because signing in
 * again produces exactly the same answer.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly operationId: OperationId;
  readonly body: unknown;

  constructor(operationId: OperationId, status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.operationId = operationId;
    this.body = body;
  }

  /** The credential failed. Signing in again is the right response. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** The credential was fine and the answer is still no. Signing in again changes nothing. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * The server's machine-readable code for the refusal — 19 Aug 2026.
   *
   * ── Why this exists ────────────────────────────────────────────────────────────────────────────
   *
   * Every refusal the edge produces is `{ code, message }` — `entitlement_required`, `forbidden`,
   * `not_found`, `invalid_request`. The `code` has been on the wire since the pipeline was written
   * and **nothing anywhere read it**: 93 render sites across 57 files print `String(error)`, which
   * resolves to `ApiError: ` + the route id + the server's English. That is how a recruiter came to
   * read `ApiError: copilot.ai_settings.read: Module "job_intelligence" is not enabled.`
   *
   * `message` is written for a developer reading a stack trace and is the right thing there. The
   * code is what a screen can turn into a sentence for the person in front of it — the same
   * separation `candidate-upload.tsx` already makes by hand off `status`, and the same one
   * `SignInError.reason` makes for the sign-in flow. This is that, generalised, so a screen does not
   * have to reach into `body` and guess at its shape.
   *
   * `undefined` when the body is not ours — a proxy's HTML, a gateway timeout page. A caller with
   * no code falls back to a generic sentence, which is correct: an unrecognised failure should not
   * be described confidently.
   */
  get problemCode(): string | undefined {
    const body = this.body;
    if (!body || typeof body !== 'object' || !('code' in body)) return undefined;
    const code = (body as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }

  /**
   * Which field the server objected to, and what it said — keyed by the request field's name.
   *
   * ── Why this exists, and what it fixes ─────────────────────────────────────────────────────────
   *
   * A 422 from the request pipeline already carries `issues: [{ path, message }]` — one entry per
   * field zod rejected, each with a message written for a person ("Expected an amount like 1200000
   * or 1200.50."). Every screen was throwing that away and rendering `body.message`, which for a
   * schema failure is the same eleven words every time: *"The request does not match the declared
   * schema."*
   *
   * So somebody who typed `12,00,000` into a salary box was told the request did not match a
   * schema, with no indication of which of the twenty fields on the form was the problem, while the
   * server had already said precisely which one and precisely what it wanted. `Field` has taken an
   * `error` prop the whole time. The information existed at both ends and nothing carried it across.
   *
   * A flat record rather than the array, because that is the shape a form needs: `errors[name]`
   * beside each field. Nested paths keep their dotted form (`shifts.0.label`), so a caller that
   * wants them can still match on the prefix.
   */
  get fieldIssues(): Record<string, string> {
    const body = this.body;
    if (!body || typeof body !== 'object' || !('issues' in body)) return {};

    const issues = (body as { issues: unknown }).issues;
    if (!Array.isArray(issues)) return {};

    const found: Record<string, string> = {};
    for (const issue of issues) {
      if (!issue || typeof issue !== 'object') continue;
      const { path, message } = issue as { path?: unknown; message?: unknown };
      // First one wins: zod can report two problems for one field, and the first is the one the
      // person hits first.
      if (typeof path === 'string' && typeof message === 'string' && !(path in found)) {
        found[path] = message;
      }
    }
    return found;
  }
}

export interface ApiClient {
  call<Id extends OperationId>(
    operationId: Id,
    input?: {
      readonly body?: OperationTypes[Id]['request'];
      /** Path parameters — the `{name}` segments of the operation's path. */
      readonly params?: Readonly<Record<string, string>>;
      /**
       * Query-string parameters: filters, cursors, page sizes.
       *
       * Separate from `params` because they are a different thing in a different place, and
       * conflating them is silent. Before this existed, a cursor passed as `params` was simply
       * dropped — `buildPath` substitutes placeholders and ignores everything else, so the first
       * page came back forever with no error anywhere.
       *
       * `undefined` values are omitted rather than serialised as the string "undefined".
       */
      readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
      readonly signal?: AbortSignal;
    },
  ): Promise<OperationTypes[Id]['response']>;
}

/**
 * `/v1/jobs/{id}` + { id } → `/v1/jobs/abc`. Throws on a parameter the caller forgot.
 *
 * Braces, not colons. The templates come from `API_OPERATIONS`, which is generated from
 * `docs/api/openapi.json`, and OpenAPI writes path parameters as `{name}` — the `:name` form
 * belongs to the route registry on the server side and never reaches this file. Substituting the
 * wrong one is silent: nothing matches, the placeholder survives URL-encoded, and the server
 * answers 422 about a path parameter that looks present.
 *
 * Hence the final check. Every parameterised route in the product goes through this function, so
 * one assertion here turns "the client and the generator disagree about template syntax" from a
 * server-side schema error into an exception naming the template.
 */
function buildPath(template: string, params: Readonly<Record<string, string>>): string {
  const path = template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Missing path parameter "${name}" for ${template}.`);
    }
    return encodeURIComponent(value);
  });

  if (/[{}]/.test(path)) {
    throw new Error(
      `Path "${template}" still contains a placeholder after substitution. The generated ` +
        'manifest and this client disagree about path-parameter syntax.',
    );
  }

  return path;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const baseUrl = options.baseUrl.replace(/\/+$/, '');

  return {
    async call(operationId, input = {}) {
      const operation = API_OPERATIONS[operationId];
      const token = await options.getToken();

      const search = new URLSearchParams();
      for (const [name, value] of Object.entries(input.query ?? {})) {
        if (value !== undefined) {
          search.set(name, String(value));
        }
      }
      const queryString = search.size > 0 ? `?${search}` : '';

      const url = `${baseUrl}${buildPath(operation.path, input.params ?? {})}${queryString}`;

      /*
       * The content type travels with the *body*, not with the method.
       *
       * This used to be `operation.mutates ? ...`, which declared JSON on every mutating call
       * including the ones that send nothing — a DELETE whose subject is entirely in the path.
       * Fastify reads that as a promise of a body and refuses the request outright:
       * "Body cannot be empty when content-type is set to 'application/json'". Every body-less
       * mutation in the product was therefore a 400 that no test had reached, because the attack
       * battery sends `{}` for mutating routes and so never produces the empty case.
       *
       * Found by the first screen to call one — the interview tracker's "reset my columns" — and
       * fixed here rather than by giving each such route a body it does not want.
       */
      const sendsBody = operation.mutates && input.body !== undefined;

      const response = await doFetch(url, {
        method: operation.method,
        headers: {
          accept: 'application/json',
          ...(sendsBody ? { 'content-type': 'application/json' } : {}),
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        ...(sendsBody ? { body: JSON.stringify(input.body) } : {}),
        signal: input.signal,
      });

      // Read the body once, whatever the status. A non-JSON error page (a proxy, a 502) must not
      // turn into a parse exception that hides the status the caller needed to see.
      const text = await response.text();
      let body: unknown;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        body = text;
      }

      if (!response.ok) {
        const detail =
          body && typeof body === 'object' && 'message' in body
            ? String((body as { message: unknown }).message)
            : response.statusText;
        throw new ApiError(operationId, response.status, body, `${operationId}: ${detail}`);
      }

      return body as never;
    },
  };
}
